import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { timeZoneOptions, currentTimeIn } from "../shared/timezones";
import {
  SEASON_LENGTHS, endsOnFor, lengthKeyFor, questionsNeeded, readInventory,
  readableDate, readableDateLong, isPast, countdownText, firstDropWindowOpens,
} from "../shared/season-plan";
import { describeWindow } from "../shared/copy";
import type { CrewPurpose } from "../shared/checkin-prompts";
import type { TimingStyle } from "../shared/scheduling";
import { configurePayload, seasonSettingsFromRow } from "../shared/season-settings";

// Setting a crew up, one decision per screen.
//
// The old host page was a workbench: every control on one wall, with
// troubleshooting bolted on because push kept breaking. That works if you
// already know the game. It does not work for someone starting their first crew
// with no one to ask, which is the person this has to serve.
//
// So: one question at a time, a bar showing there is an end to it, and nothing
// locked. Every step saves as you leave it, so a host who closes the tab comes
// back to what they had rather than starting over.

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MIN_CHIPS = [5, 10, 20, 30];
const HOUR_CHIPS = [1, 2, 3, 4];

export interface WizardState {
  crewName: string;
  timezone: string;
  purpose: CrewPurpose;
  startsOn: string;
  endsOn: string;
  activeWeekdays: number[];
  dropsPerDay: number;
  timingStyle: TimingStyle;
  windowStart: string;
  windowEnd: string;
  exactTimes: string[];
  questionsPerDrop: number;
  playWindowMinutes: number;
  lastCall: boolean;
  weekWinBonus: number;
  lifelines: number;
}

interface Props {
  leagueId: string;
  initial: WizardState;
  status: any;
  onReload: () => Promise<void>;
}

const STEPS = ["Crew", "Purpose", "Dates", "Drops", "Invite", "Go live"] as const;

export function HostWizard({ leagueId, initial, status, onReload }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [v, setV] = useState<WizardState>(initial);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [unit, setUnit] = useState<"min" | "hr">(initial.playWindowMinutes < 60 ? "min" : "hr");
  const [crewLink, setCrewLink] = useState<string | null>(null);

  async function makeCrewLink() {
    setBusy(true); setErr("");
    try {
      const r = await api<{ url: string }>(`/api/league/admin`, { body: { op: "crew-link", leagueId } });
      setCrewLink(r.url);
    } catch (e: any) {
      setErr(e?.message ?? "Couldn't make the link. Try again.");
    } finally {
      setBusy(false);
    }
  }
  // "Today" in the crew's own zone, so a host setting up at 9pm in Los Angeles
  // is not told their start date is in the past.
  const today = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: v.timezone, year: "numeric", month: "2-digit", day: "2-digit",
      }).format(new Date());
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  }, [v.timezone]);

  // Re-render once a minute so the countdown stays true. Anything faster would
  // be a stopwatch nobody asked for.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const opensIso = useMemo(
    () => firstDropWindowOpens({
      startsOn: v.startsOn, endsOn: v.endsOn, activeWeekdays: v.activeWeekdays,
      windowStart: v.timingStyle === "exact" ? (v.exactTimes[0] ?? v.windowStart) : v.windowStart,
      timezone: v.timezone,
    }),
    [v.startsOn, v.endsOn, v.activeWeekdays, v.windowStart, v.exactTimes, v.timingStyle, v.timezone]
  );

  const set = <K extends keyof WizardState>(k: K, val: WizardState[K]) =>
    setV((s) => ({ ...s, [k]: val }));

  const members = (status?.members ?? []).filter((m: any) => m.inviteStatus !== "removed");
  const approved = members.reduce((n: number, m: any) => n + (m.readyCount ?? 0), 0);
  const needed = useMemo(() => questionsNeeded(v), [v]);
  const inventory = readInventory(needed, approved);
  const readyMembers = members.filter((m: any) => m.checkedIn && m.readyCount > 0).length;

  // Every step saves on the way out. Closing the tab halfway through should cost
  // a host nothing.
  async function saveSettings(): Promise<boolean> {
    setBusy(true); setErr("");
    try {
      await api(`/api/league/admin`, {
        body: { op: "update", leagueId, name: v.crewName, timezone: v.timezone },
      });
      // The same payload builder the host tools use. The wizard used to list
      // its own fields here and left out the answer timer, the check-in cadence
      // and the wildcard days, so finishing setup reset whatever had been set
      // for those.
      const r = await api<{ note?: string }>(`/api/league/admin`, {
        body: {
          op: "configure", leagueId,
          ...configurePayload({ ...seasonSettingsFromRow(status?.season), ...v }),
        },
      });
      setNote(r?.note ?? "");
      await onReload();
      return true;
    } catch (e: any) {
      setErr(e?.message ?? "That didn't save. Check your connection and try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (await saveSettings()) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  async function goLive(startNow: boolean) {
    if (!(await saveSettings())) return;
    setBusy(true); setErr("");
    try {
      const r = await api<{ note?: string }>(`/api/league/admin`, { body: { op: "activate", leagueId } });
      if (startNow) {
        await api(`/api/league/admin`, { body: { op: "drop-now", leagueId } });
      }
      setNote(r?.note ?? "");
      await onReload();
    } catch (e: any) {
      setErr(e?.message ?? "Couldn't start the season. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function toggleDay(d: number) {
    set("activeWeekdays", v.activeWeekdays.includes(d)
      ? v.activeWeekdays.filter((x) => x !== d)
      : [...v.activeWeekdays, d].sort());
  }

  const lengthKey = lengthKeyFor(v.startsOn, v.endsOn);
  const windowHours = (() => {
    const [sh, sm] = v.windowStart.split(":").map(Number);
    const [eh, em] = v.windowEnd.split(":").map(Number);
    return Math.max(0, (eh * 60 + em - (sh * 60 + sm)) / 60);
  })();
  const canAdvance =
    step === 0 ? v.crewName.trim().length > 0
    : step === 2 ? !!v.startsOn && !!v.endsOn && v.endsOn >= v.startsOn
    : step === 3 ? v.activeWeekdays.length > 0
    : true;

  return (
    <div className="app">
      <div className="header">
        <h1>Set up your crew</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Crew home</button>
      </div>

      <div className="progress" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
        {STEPS.map((s, i) => <span key={s} className={`progress-pip${i <= step ? " done" : ""}`} />)}
      </div>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        Step {step + 1} of {STEPS.length} · {STEPS[step]}. Nothing is locked in, you can change all of this later.
      </p>

      {/* ---- 1. Name ---- */}
      {step === 0 && (
        <div className="card">
          <span className="tag">What's this crew called?</span>
          <p className="muted">Whatever your people already call themselves.</p>
          <input className="input" value={v.crewName} placeholder="The Cousins"
                 onChange={(e) => set("crewName", e.target.value)} />
          <label className="muted">Timezone (drop times follow this)</label>
          <select className="input" value={v.timezone} onChange={(e) => set("timezone", e.target.value)}>
            {timeZoneOptions(v.timezone).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
          </select>
          {currentTimeIn(v.timezone) && <p className="muted">Right now it is {currentTimeIn(v.timezone)} there.</p>}
          <p className="muted">
            Everybody plays at the same moment, wherever they are. Pick the zone most of your
            crew is in.
          </p>
        </div>
      )}

      {/* ---- 2. Purpose ---- */}
      {step === 1 && (
        <div className="card">
          <span className="tag">What's this crew for?</span>
          <p className="muted">
            This changes what your people get asked at check-in. Both go the same depth,
            they just start from different places.
          </p>
          <div className="grid" style={{ gap: 8 }}>
            {([
              { key: "keeping_up", title: "Keeping up",
                blurb: "People who already know each other and want to stay current." },
              { key: "getting_to_know", title: "Getting to know each other",
                blurb: "A group that's still new to each other." },
            ] as { key: CrewPurpose; title: string; blurb: string }[]).map((o) => (
              <button key={o.key} className={`choice${v.purpose === o.key ? " current" : ""}`}
                      aria-pressed={v.purpose === o.key} onClick={() => set("purpose", o.key)}>
                <span className="choice-title">{o.title}</span>
                <span className="choice-blurb">{o.blurb}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- 3. Dates ---- */}
      {step === 2 && (
        <div className="card">
          <span className="tag">How long is the season?</span>
          <div className="chips">
            {SEASON_LENGTHS.map((l) => (
              <button key={l.key}
                      className={`chip${lengthKey === l.key ? " current" : ""}`}
                      onClick={() => { if (l.days) set("endsOn", endsOnFor(v.startsOn, l.days)); }}>
                {l.label}
              </button>
            ))}
          </div>
          <label className="muted">Starts</label>
          <input className="input" type="date" value={v.startsOn}
                 onChange={(e) => {
                   const startsOn = e.target.value;
                   const keep = SEASON_LENGTHS.find((l) => l.key === lengthKey);
                   setV((s) => ({ ...s, startsOn, endsOn: keep?.days ? endsOnFor(startsOn, keep.days) : s.endsOn }));
                 }} />
          <label className="muted">Ends</label>
          <input className="input" type="date" value={v.endsOn}
                 onChange={(e) => set("endsOn", e.target.value)} />
          {v.startsOn && v.endsOn && (
            <p className="muted">
              <b>{readableDate(v.startsOn)} to {readableDate(v.endsOn)}</b>
            </p>
          )}
          {isPast(v.startsOn, today) && (
            <p className="error">That start date has already passed. Pick a later one.</p>
          )}
          <p className="muted">
            The finale runs itself on the end date. You never have to close the season by hand.
          </p>
        </div>
      )}

      {/* ---- 4. Drops ---- */}
      {step === 3 && (
        <>
          <div className="card">
            <span className="tag">Which days?</span>
            <div className="chips">
              {WEEKDAYS.map((w, i) => (
                <button key={w} className={`chip${v.activeWeekdays.includes(i) ? " current" : ""}`}
                        onClick={() => toggleDay(i)}>{w}</button>
              ))}
            </div>
            {v.activeWeekdays.length === 0 && <p className="error">Pick at least one day.</p>}
          </div>

          <div className="card">
            <span className="tag">How many drops a day?</span>
            <Stepper value={v.dropsPerDay} min={1} max={9} onChange={(n) => set("dropsPerDay", n)} />
          </div>

          <div className="card">
            <span className="tag">When do they land?</span>
            <div className="grid" style={{ gap: 8 }}>
              <button className={`choice${v.timingStyle === "surprise" ? " current" : ""}`}
                      aria-pressed={v.timingStyle === "surprise"} onClick={() => set("timingStyle", "surprise")}>
                <span className="choice-title">Surprise</span>
                <span className="choice-blurb">A random moment between the hours you pick.</span>
              </button>
              <button className={`choice${v.timingStyle === "exact" ? " current" : ""}`}
                      aria-pressed={v.timingStyle === "exact"} onClick={() => set("timingStyle", "exact")}>
                <span className="choice-title">A set time</span>
                <span className="choice-blurb">Same time every day, so the crew knows when to be ready.</span>
              </button>
            </div>

            {v.timingStyle === "surprise" ? (
              <>
                <label className="muted">Between</label>
                <div className="row">
                  <input className="input" type="time" value={v.windowStart}
                         onChange={(e) => set("windowStart", e.target.value)} />
                  <span className="muted">and</span>
                  <input className="input" type="time" value={v.windowEnd}
                         onChange={(e) => set("windowEnd", e.target.value)} />
                </div>
              </>
            ) : (
              <>
                <label className="muted">{v.dropsPerDay === 1 ? "At" : "At these times"}</label>
                {Array.from({ length: v.dropsPerDay }, (_, i) => (
                  <input key={i} className="input" type="time"
                         value={v.exactTimes[i] ?? v.windowStart}
                         onChange={(e) => {
                           const times = [...v.exactTimes];
                           while (times.length < v.dropsPerDay) times.push(v.windowStart);
                           times[i] = e.target.value;
                           set("exactTimes", times);
                         }} />
                ))}
              </>
            )}
            <p className="muted">
              Everyone gets it at the same moment, wherever they are. Noon where you are is
              9am on the west coast, so pick something the whole crew can catch.
            </p>
            {v.timingStyle === "surprise" && (
              <p className="muted">
                {windowHours >= 6
                  ? "That's a wide window. A drop could land while people are asleep or at work, and the clock starts whether they saw it or not. A couple of hours around lunch tends to catch more people."
                  : "Good window. Nobody knows the minute, everybody knows roughly when."}
              </p>
            )}
          </div>

          <div className="card">
            <span className="tag">Questions per drop</span>
            <Stepper value={v.questionsPerDrop} min={1} max={9} onChange={(n) => set("questionsPerDrop", n)} />
          </div>

          <div className="card">
            <span className="tag">How long to answer?</span>
            <p className="muted">Once a drop lands. This is separate from when it lands.</p>
            <div className="seg" role="group" aria-label="Units">
              {(["min", "hr"] as const).map((u) => (
                <button key={u} className={`seg-btn${unit === u ? " current" : ""}`}
                        aria-pressed={unit === u} onClick={() => setUnit(u)}>
                  {u === "min" ? "Minutes" : "Hours"}
                </button>
              ))}
            </div>
            <div className="chips">
              {(unit === "min" ? MIN_CHIPS : HOUR_CHIPS).map((n) => {
                const mins = unit === "min" ? n : n * 60;
                return (
                  <button key={n} className={`chip${v.playWindowMinutes === mins ? " current" : ""}`}
                          onClick={() => set("playWindowMinutes", mins)}>{n}</button>
                );
              })}
            </div>
            <label className="muted">Or type it ({unit === "min" ? "minutes" : "hours"})</label>
            <input
              className="input" type="number" inputMode="numeric"
              min={unit === "min" ? 5 : 1} max={unit === "min" ? 240 : 24}
              value={unit === "min" ? v.playWindowMinutes : +(v.playWindowMinutes / 60).toFixed(2)}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n)) return;
                set("playWindowMinutes", Math.max(5, Math.round(unit === "min" ? n : n * 60)));
              }}
            />
            <p className="muted">Everyone gets {describeWindow(v.playWindowMinutes)} to play. Five minutes is the shortest.</p>
            <label className="row" style={{ gap: 10, alignItems: "flex-start" }}>
              <input type="checkbox" checked={v.lastCall} onChange={(e) => set("lastCall", e.target.checked)} />
              <span>
                <b>Last call ping</b>
                <span className="choice-blurb" style={{ display: "block" }}>
                  Nudge anyone who hasn&apos;t played about half an hour before the window shuts.
                  Keeps the crew from waiting on one person.
                </span>
              </span>
            </label>
          </div>

          <div className="card">
            <span className="tag">Winning a week</span>
            <p className="muted">
              Every week has its own winner, worth this many extra points. It means a slow
              start isn&apos;t the end of somebody&apos;s season. Set it to zero for one long race.
            </p>
            <Stepper value={v.weekWinBonus} min={0} max={10} onChange={(n) => set("weekWinBonus", n)} />
            <p className="muted">
              {v.weekWinBonus === 0
                ? "Off. Season points only."
                : `Win a week, get ${v.weekWinBonus} point${v.weekWinBonus === 1 ? "" : "s"}.`}
            </p>
          </div>

          <div className="card">
            <span className="tag">50/50s</span>
            <p className="muted">
              How many times each person can knock out two wrong answers, across the whole
              season. Everybody gets the same number, whether they&apos;re winning or not.
            </p>
            <Stepper value={v.lifelines} min={0} max={10} onChange={(n) => set("lifelines", n)} />
            <p className="muted">
              {v.lifelines === 0 ? "Off. Straight answers only." : `${v.lifelines} each, all season.`}
            </p>
          </div>

          <InventoryCard readout={inventory} />
        </>
      )}

      {/* ---- 5. Invite ---- */}
      {step === 4 && (
        <>
          <div className="card">
            <span className="tag">Get your people in</span>
            <p className="muted">
              One link for the whole crew. Drop it in the group chat and anyone who opens it
              joins. It keeps working, so nobody is left out for tapping it late.
            </p>
            {crewLink ? (
              <>
                <input className="input" readOnly value={crewLink} onFocus={(e) => e.target.select()} />
                <button className="btn primary" onClick={async () => {
                  try { await navigator.clipboard.writeText(crewLink); setNote("Copied. Paste it in the group chat."); }
                  catch { setNote("Copy it from the box above."); }
                }}>Copy the link</button>
              </>
            ) : (
              <button className="btn primary" disabled={busy} onClick={() => void makeCrewLink()}>
                Make the crew link
              </button>
            )}
            <p className="muted" style={{ fontSize: 13 }}>
              Adding one person by email instead is under Crew, once you&apos;re set up.
            </p>
          </div>
          <div className="card">
            <span className="tag">Who&apos;s in so far</span>
            {members.length === 0 ? (
              <p className="muted">Just you for now.</p>
            ) : members.map((m: any) => (
              <div className="lb-row" key={m.userId}>
                <span className="lb-name">{m.name}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {m.inviteStatus === "joined" ? "In" : "Invited"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ---- 6. Go live ---- */}
      {step === 5 && (
        <>
          <div className="card">
            <span className="tag">Warming up</span>
            <p>
              <b>{readyMembers} of {members.length}</b> {members.length === 1 ? "person is" : "people are"} ready.
            </p>
            <div className="progress">
              {members.map((m: any) => (
                <span key={m.userId} className={`progress-pip${m.checkedIn && m.readyCount > 0 ? " done" : ""}`} />
              ))}
            </div>
            <p className="muted">
              Ready means they&apos;ve checked in and approved at least one question. You can start
              without everybody. Anyone who checks in later gets pulled straight in.
            </p>
          </div>

          <InventoryCard readout={inventory} />

          <div className="card">
            <span className="tag">Start the season</span>
            {isPast(v.startsOn, today) ? (
              // A draft set up a while ago has a start date that has already
              // gone by. Saying "drops begin on <a date last week>" would read
              // as broken, and quietly starting is worse.
              <p className="muted">
                Your start date, {readableDate(v.startsOn)}, has already passed. Pick a new one
                back on Dates, or just start now.
              </p>
            ) : (
              <>
                <div className="countdown">
                  <span className="countdown-label">First drop window opens in</span>
                  <span className="countdown-value">
                    {opensIso ? countdownText(new Date(opensIso).getTime(), tick) : "not set yet"}
                  </span>
                  <span className="countdown-date">{readableDateLong(v.startsOn)}</span>
                </div>
                <p className="muted">
                  That is when the window opens. The drop itself lands at a moment inside it that
                  nobody sees coming, you included. Or kick it off right now with the first drop.
                </p>
              </>
            )}
            {err && <p className="error">{err}</p>}
            <button className="btn primary" disabled={busy || isPast(v.startsOn, today)}
                    onClick={() => void goLive(false)}>
              {busy ? "Working…" : "Schedule it"}
            </button>
            <button className="btn ghost" disabled={busy} onClick={() => void goLive(true)}>
              Start now with a drop
            </button>
          </div>
        </>
      )}

      {note && <p className="muted">{note}</p>}
      {err && step !== 5 && <p className="error">{err}</p>}

      <div className="row">
        {step > 0 && (
          <button className="btn ghost" disabled={busy} onClick={() => setStep((s) => s - 1)}>Back</button>
        )}
        {step < STEPS.length - 1 && (
          <button className="btn primary" disabled={busy || !canAdvance} onClick={() => void next()}>
            {busy ? "Saving…" : "Next"}
          </button>
        )}
      </div>
    </div>
  );
}

function Stepper({ value, min, max, onChange }: { value: number; min: number; max: number; onChange: (n: number) => void }) {
  return (
    <div className="stepper">
      <button className="stepper-btn" aria-label="Fewer" disabled={value <= min}
              onClick={() => onChange(Math.max(min, value - 1))}>−</button>
      <span className="stepper-value">{value}</span>
      <button className="stepper-btn" aria-label="More" disabled={value >= max}
              onClick={() => onChange(Math.min(max, value + 1))}>+</button>
    </div>
  );
}

// The math a host would otherwise only discover when a drop fires and finds
// nothing to ask.
function InventoryCard({ readout }: { readout: ReturnType<typeof readInventory> }) {
  return (
    <div className={`card inventory ${readout.verdict}`}>
      <span className="tag">{readout.headline}</span>
      <p className="muted">{readout.detail}</p>
    </div>
  );
}
